import { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '../i18n';

const LOCALE_MAP = {
    da: 'da-DK', en: 'en-US', de: 'de-DE', sv: 'sv-SE', no: 'nb-NO',
};

const SILENCE_MS = 3000; // auto-submit after 3s of silence

/**
 * VoiceButton with 3-second silence auto-send.
 *
 * Duplication fix (important):
 *   Android Chrome's SpeechRecognition appends every interim/final hypothesis
 *   as a SEPARATE, cumulative entry in `event.results`. Looping over the whole
 *   array and concatenating piled words up ("whenwhenwhen do I plant…").
 *   On Android the LAST entry already contains the full phrase, so we read ONLY
 *   `results[results.length - 1]` and never accumulate. `continuous` stays true
 *   so the mic keeps listening reliably (continuous=false stopped too early on
 *   some Android devices).
 *
 * Props:
 *   onTranscript(text) — live transcript for the input box
 *   onAutoSend(text)   — fires after SILENCE_MS of silence
 *   locale             — speech-recognition language
 *   disabled
 */
export default function VoiceButton({ onTranscript, onAutoSend, locale = 'en', disabled = false }) {
    const { t } = useI18n();
    const [supported, setSupported] = useState(false);
    const [listening, setListening] = useState(false);
    const recogRef = useRef(null);
    const silenceTimer = useRef(null);
    const transcriptRef = useRef('');

    useEffect(() => {
        const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (Rec) setSupported(true);
    }, []);

    const clearSilenceTimer = () => {
        if (silenceTimer.current) {
            clearTimeout(silenceTimer.current);
            silenceTimer.current = null;
        }
    };

    const stop = useCallback(() => {
        clearSilenceTimer();
        try { recogRef.current?.stop(); } catch { /* ignore */ }
        setListening(false);
    }, []);

    const triggerAutoSend = useCallback(() => {
        const finalText = transcriptRef.current.trim();
        stop();
        if (finalText && onAutoSend) {
            onAutoSend(finalText);
        }
        transcriptRef.current = '';
    }, [onAutoSend, stop]);

    const start = () => {
        const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!Rec) return;
        const r = new Rec();
        r.lang = LOCALE_MAP[locale] || 'en-US';
        r.interimResults = true;
        r.continuous = true;
        r.maxAlternatives = 1;
        transcriptRef.current = '';
        r.onresult = (e) => {
            // Read ONLY the latest hypothesis — never concatenate across entries.
            // (Android delivers cumulative results; the last one is the full phrase.)
            const last = e.results[e.results.length - 1];
            const text = (last && last[0] ? last[0].transcript : '').trim();
            transcriptRef.current = text;
            onTranscript?.(text);
            // Reset silence timer on every chunk
            clearSilenceTimer();
            if (onAutoSend) {
                silenceTimer.current = setTimeout(triggerAutoSend, SILENCE_MS);
            }
        };
        r.onend = () => {
            clearSilenceTimer();
            setListening(false);
        };
        r.onerror = (ev) => {
            clearSilenceTimer();
            setListening(false);
            const err = ev?.error;
            if (err === 'not-allowed' || err === 'service-not-allowed') {
                toast.error(t('voice.blocked'));
            } else if (err && err !== 'no-speech' && err !== 'aborted') {
                toast.error(`${t('voice.failed')} (${err})`);
            }
        };
        try {
            r.start();
            recogRef.current = r;
            setListening(true);
        } catch {
            setListening(false);
        }
    };

    useEffect(() => () => { clearSilenceTimer(); try { recogRef.current?.stop(); } catch { /* ignore */ } }, []);

    if (!supported) return null;

    return (
        <button
            type="button"
            data-testid="voice-btn"
            onClick={listening ? stop : start}
            disabled={disabled}
            aria-label={listening ? 'Stop voice input' : 'Start voice input'}
            title={listening ? 'Listening… auto-sends after 3s of silence' : 'Voice input'}
            className={`p-2 rounded-full transition-colors ${
                listening
                    ? 'bg-brand-terracotta text-white animate-pulse'
                    : 'hover:bg-brand-bg2 text-brand-ink2'
            }`}
        >
            {listening ? <MicOff size={18} strokeWidth={1.8} /> : <Mic size={18} strokeWidth={1.8} />}
        </button>
    );
}
